/**
 * Single source of truth for the demo portfolio's headline numbers.
 *
 * Analytics, app health, review summary and AI insight fixtures all read from here so the
 * story stays consistent across screens (e.g. the Acme Shopping crash spike after the v4.12.0
 * release shows up in the crash-rate chart, the health page, the 1-star reviews and the AI
 * crash summary alike).
 *
 * Numbers describe the "THIS_MONTH" window (last 30 days, ending today).
 */

export interface DemoAppProfile {
  id: number;
  name: string;
  /** Monthly active users. */
  mau: number;
  newUsers: number;
  sessions: number;
  avgSessionSeconds: number;
  /** 30-day retention, percent. */
  retentionRate: number;
  /** Percent of sessions without a crash. */
  crashFreeSessions: number;
  /** Baseline ANR rate, percent of sessions. */
  anrBaseline: number;
  /** Month-over-month change, percent. */
  trends: { activeUsers: number; newUsers: number; sessions: number; sessionDuration: number };
  /** Share of active users on Android (0-1). */
  androidShare: number;
  /** [country, weight] — weights need not sum to 1. */
  countries: Array<[string, number]>;
  /** Day-of-week multiplier, Sunday first. */
  weekday: [number, number, number, number, number, number, number];
  /** Monthly churn, percent, and its change vs last month in percentage points. */
  churn: { rate: number; changePts: number };
  /** Busiest local time window, e.g. '8-10 PM'. */
  peakHours: string;
  /** Day-over-day wobble amplitude (0-1). */
  noise: number;
  /** Days of data missing at the tail (sync broken). */
  staleDays: number;
  /** Incoming reviews per store: lifetime totals shown on the Review Summary page. */
  reviewSummary: {
    /** [1★, 2★, 3★, 4★, 5★] counts. */
    starCounts: [number, number, number, number, number];
    sentiment: { positive: number; neutral: number; negative: number };
    platforms: Array<{ platform: 'GOOGLE_PLAY' | 'APP_STORE'; averageRating: number; totalReviews: number }>;
  };
}

export const DEMO_PROFILES: Record<number, DemoAppProfile> = {
  8001: {
    id: 8001,
    name: 'Acme Shopping',
    mau: 64_200,
    newUsers: 11_850,
    sessions: 231_000,
    avgSessionSeconds: 312,
    retentionRate: 47.3,
    crashFreeSessions: 99.1,
    anrBaseline: 0.25,
    trends: { activeUsers: 8.4, newUsers: 12.1, sessions: 9.6, sessionDuration: 2.3 },
    androidShare: 0.58,
    countries: [
      ['United States', 41],
      ['United Kingdom', 14],
      ['Canada', 9.5],
      ['Germany', 7.5],
      ['Australia', 6],
      ['India', 5.5],
      ['France', 4.5],
      ['Brazil', 3.5],
      ['Netherlands', 2.5],
      ['Other', 6],
    ],
    // Shopping peaks on the weekend and Sunday evening.
    weekday: [1.18, 0.94, 0.92, 0.95, 0.98, 1.06, 1.17],
    churn: { rate: 4.9, changePts: -0.4 },
    peakHours: '8 - 10 PM',
    noise: 0.05,
    staleDays: 0,
    reviewSummary: {
      starCounts: [412, 318, 640, 2_980, 8_130],
      sentiment: { positive: 11_110, neutral: 640, negative: 730 },
      platforms: [
        { platform: 'GOOGLE_PLAY', averageRating: 4.4, totalReviews: 7_420 },
        { platform: 'APP_STORE', averageRating: 4.5, totalReviews: 5_060 },
      ],
    },
  },
  8002: {
    id: 8002,
    name: 'Acme Loyalty',
    mau: 28_900,
    newUsers: 3_120,
    sessions: 118_400,
    avgSessionSeconds: 184,
    retentionRate: 58.6,
    crashFreeSessions: 99.6,
    anrBaseline: 0.15,
    trends: { activeUsers: 3.1, newUsers: 1.8, sessions: 4.2, sessionDuration: -0.9 },
    androidShare: 1,
    countries: [
      ['United States', 52],
      ['Canada', 17],
      ['United Kingdom', 11],
      ['Australia', 8],
      ['Germany', 4.5],
      ['Other', 7.5],
    ],
    // Loyalty is checked at the till: weekday lunchtimes and Friday/Saturday.
    weekday: [0.9, 1.0, 1.02, 1.0, 1.02, 1.1, 0.96],
    churn: { rate: 2.8, changePts: -0.2 },
    peakHours: '12 - 1 PM',
    noise: 0.04,
    staleDays: 0,
    reviewSummary: {
      starCounts: [96, 88, 240, 1_310, 4_820],
      sentiment: { positive: 6_130, neutral: 240, negative: 184 },
      platforms: [{ platform: 'GOOGLE_PLAY', averageRating: 4.6, totalReviews: 6_554 }],
    },
  },
  8003: {
    id: 8003,
    name: 'Acme Delivery Partner',
    mau: 9_450,
    newUsers: 1_680,
    sessions: 142_000,
    avgSessionSeconds: 461,
    retentionRate: 71.2,
    crashFreeSessions: 99.1,
    anrBaseline: 0.55,
    trends: { activeUsers: 24.6, newUsers: 31.0, sessions: 27.3, sessionDuration: 5.8 },
    androidShare: 0,
    countries: [
      ['United States', 63],
      ['Canada', 21],
      ['United Kingdom', 10],
      ['Other', 6],
    ],
    // Couriers work the dinner rush and the weekend.
    weekday: [1.02, 0.86, 0.88, 0.94, 1.02, 1.22, 1.06],
    churn: { rate: 3.6, changePts: 0.3 },
    peakHours: '6 - 8 PM',
    noise: 0.06,
    staleDays: 0,
    reviewSummary: {
      starCounts: [188, 154, 290, 720, 1_630],
      sentiment: { positive: 2_350, neutral: 290, negative: 342 },
      platforms: [{ platform: 'APP_STORE', averageRating: 4.2, totalReviews: 2_982 }],
    },
  },
  8004: {
    id: 8004,
    name: 'Acme Rewards (Beta)',
    mau: 640,
    newUsers: 85,
    sessions: 2_950,
    avgSessionSeconds: 142,
    retentionRate: 34.0,
    crashFreeSessions: 95.8,
    anrBaseline: 1.4,
    trends: { activeUsers: -18.2, newUsers: -40.5, sessions: -22.0, sessionDuration: -6.1 },
    androidShare: 1,
    countries: [
      ['United States', 58],
      ['Canada', 22],
      ['United Kingdom', 12],
      ['Other', 8],
    ],
    weekday: [0.8, 1.05, 1.08, 1.06, 1.04, 1.0, 0.85],
    churn: { rate: 11.4, changePts: 2.1 },
    peakHours: '7 - 9 PM',
    noise: 0.12,
    // Google Play sync broke 9 days ago (see apps.fixtures), so the tail of the series is empty.
    staleDays: 9,
    reviewSummary: {
      starCounts: [9, 7, 12, 14, 12],
      sentiment: { positive: 26, neutral: 12, negative: 16 },
      platforms: [{ platform: 'GOOGLE_PLAY', averageRating: 3.2, totalReviews: 54 }],
    },
  },
};

/** Apps whose numbers roll into the "All Apps" aggregate (the inactive beta is excluded). */
export const AGGREGATE_APP_IDS = [8001, 8002, 8003];

/** Ordered list of profiles, handy for iteration. */
export const DEMO_PROFILE_LIST: DemoAppProfile[] = Object.values(DEMO_PROFILES);
